import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import PartnerPlanWall from '../components/PartnerPlanWall';
import { canCreatePartnerGroupTrips, formatPlanLimit, planNameKey } from '../utils/partnerPlan';
import PartnerWorkspaceFooter from '../components/PartnerWorkspaceFooter';
import { PartnerPageHeader } from '../components/PartnerListChrome';
import PartnerMobileHome from '../components/PartnerMobileHome';
import {
  DOCUMENT,
  documentSectionTitleSx,
  documentSubSectionTitleSx,
} from '../constants/documentTheme';
import { getPartnerWorkspaceBrand } from '../utils/partnerWorkspaceBrand';
import { clearClientSessionBranding } from '../contexts/ClientBrandingContext';
import { developerPublicBase } from '../utils/developerHost';
import { partnerMobileBottomPadSx, PARTNER_SIDEBAR_WIDTH } from '../utils/partnerWorkspace';
import usePartnerMobileLayout from '../hooks/usePartnerMobileLayout';
import { buildPartnerOpsHomeModel } from '../utils/partnerOpsHome';
import { parseTripDate } from '../utils/tripUtils';
import PartnerNeedsAttention from '../components/PartnerNeedsAttention';
import PartnerTripRequestDrawer from '../components/PartnerTripRequestDrawer';

const PartnerDesktopOpsSection = ({
  trips,
  bookings,
  inquiries,
  awaitingPayment,
  publishedPackages,
  flightAlerts = 0,
  openRequests = null,
  sectionHeadSx,
  navigate,
  t,
}) => {
  const ops = buildPartnerOpsHomeModel({ trips, bookings, inquiries, flightAlerts });
  const requestCount = openRequests == null ? ops.clientMessages : openRequests;
  const tiles = [
    {
      id: 'traveling',
      label: t('partnerDash.opsTraveling'),
      value: ops.clientsTraveling,
      go: () => navigate('/partner/trips?ops=traveling'),
    },
    {
      id: 'departures',
      label: t('partnerDash.opsDepartures'),
      value: ops.upcomingDepartures,
      go: () => navigate('/partner/trips?ops=departing'),
    },
    {
      id: 'approvals',
      label: t('partnerDash.opsApprovals'),
      value: ops.approvalsWaiting,
      go: () => navigate('/partner/bookings'),
    },
    {
      id: 'unpaid',
      label: t('partnerDash.opsUnpaid'),
      value: awaitingPayment,
      go: () => navigate('/partner/bookings'),
    },
    {
      id: 'requests',
      label: t('partnerDash.opsRequests'),
      value: requestCount,
      go: () => navigate('/partner/requests'),
    },
    {
      id: 'flights',
      label: t('partner.opsHome.metricFlights'),
      value: flightAlerts,
      go: () => navigate('/partner/trips?ops=flights'),
    },
    {
      id: 'packages',
      label: t('partnerDash.opsPackagesLive'),
      value: publishedPackages,
      go: () => navigate('/partner/packages'),
    },
  ];

  return (
    <Box sx={{ mt: 4 }} data-testid="partner-ops-dashboard">
      <Box sx={sectionHeadSx}>
        <Typography component="h2" sx={documentSectionTitleSx}>
          {t('partnerDash.opsTitle')}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
          {t('partnerDash.opsSub')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: '1px',
          bgcolor: DOCUMENT.ruleHard,
          border: `1px solid ${DOCUMENT.ruleHard}`,
          borderTop: 0,
        }}
      >
        {tiles.map((tile) => (
          <Box
            key={tile.id}
            component="button"
            type="button"
            data-testid={`partner-ops-tile-${tile.id}`}
            onClick={tile.go}
            sx={{
              bgcolor: DOCUMENT.surface,
              p: '20px 18px',
              textAlign: 'left',
              border: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: 'inherit',
              '&:hover': { bgcolor: '#F5F0E6' },
            }}
          >
            <Typography sx={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
              {tile.label}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 28, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {tile.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = usePartnerMobileLayout();
  const [partner, setPartner] = useState(null);
  const [allTrips, setAllTrips] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [allInquiries, setAllInquiries] = useState([]);
  const [flightAlertCount, setFlightAlertCount] = useState(0);
  const [openRequestCount, setOpenRequestCount] = useState(null);
  const [attentionItems, setAttentionItems] = useState([]);
  const [attentionLoading, setAttentionLoading] = useState(false);
  const [selectedAttention, setSelectedAttention] = useState(null);
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalTrips: 0,
    activeTrips: 0,
    upcomingTrips: 0,
    pastTrips: 0
  });
  const [activityStats, setActivityStats] = useState({
    pendingInquiries: 0,
    unreadMessages: 0,
    pendingBookings: 0,
    awaitingPayment: 0,
    confirmedBookings: 0,
    totalBookings: 0
  });
  const [commissionSummary, setCommissionSummary] = useState({
    pending_commission_owed: 0,
    invoiced_commission: 0,
    total_booking_value: 0
  });
  const [financeSummary, setFinanceSummary] = useState({
    bookingsThisMonth: 0,
    outstandingClientPayments: 0,
    expectedCommissions: 0,
  });
  const [packageStats, setPackageStats] = useState({
    totalPackages: 0,
    publishedPackages: 0
  });
  const [recentTrips, setRecentTrips] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [recentPackages, setRecentPackages] = useState([]);
  const [planWallOpen, setPlanWallOpen] = useState(false);
  const [planWallReason, setPlanWallReason] = useState('billing');

  useEffect(() => {
    fetchPartnerProfile();
    fetchPartnerStats();
    fetchActivityStats();
    fetchRecentPackages();
    fetchCommissionSummary();
    fetchFinanceSummary();
    fetchPartnerPlan();
    fetchOperationsAttention();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOperationsAttention = async () => {
    try {
      setAttentionLoading(true);
      const [summaryRes, attentionRes] = await Promise.all([
        api.get('/partners/operations/summary', { usePartnerAuth: true }),
        api.get('/partners/operations/attention', { usePartnerAuth: true }),
      ]);
      if (typeof summaryRes.data?.openRequests === 'number') {
        setOpenRequestCount(summaryRes.data.openRequests);
      }
      setAttentionItems(attentionRes.data?.items || []);
    } catch (err) {
      console.error('Error fetching operations attention:', err);
      setAttentionItems([]);
    } finally {
      setAttentionLoading(false);
    }
  };

  const fetchPartnerProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('partnerToken');
      
      if (!token) {
        navigate('/partner/login');
        return;
      }

      const response = await api.get('/partners/profile', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setPartner(response.data.partner);
      // Update localStorage so Navbar has fresh data
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
    } catch (err) {
      console.error('Error fetching partner profile:', err);
      if (err.response?.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('partnerToken');
        localStorage.removeItem('partner');
        navigate('/partner/login');
      } else {
        setError('Failed to load profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerPlan = async () => {
    try {
      const response = await api.get('/partners/plan');
      setEntitlements(response.data.entitlements);
    } catch (err) {
      console.error('Error fetching partner plan:', err);
    }
  };

  const fetchPartnerStats = async () => {
    try {
      const token = localStorage.getItem('partnerToken');
      
      if (!token) {
        return;
      }

      const response = await api.get('/partners/trips', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const trips = response.data.trips || [];
      setAllTrips(trips);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activeTrips = trips.filter(trip => {
        const startDate = new Date(trip.start_date);
        const endDate = new Date(trip.end_date);
        return startDate <= today && endDate >= today;
      });

      const upcomingTrips = trips.filter(trip => {
        const startDate = new Date(trip.start_date);
        return startDate > today;
      });

      const pastTrips = trips.filter(trip => {
        const endDate = new Date(trip.end_date);
        return endDate < today;
      });

      setStats({
        totalTrips: trips.length,
        activeTrips: activeTrips.length,
        upcomingTrips: upcomingTrips.length,
        pastTrips: pastTrips.length
      });
      
      // Set recent trips (max 5, sorted by start date)
      const sortedTrips = [...trips].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setRecentTrips(sortedTrips.slice(0, 5));
    } catch (err) {
      console.error('Error fetching partner stats:', err);
      // Don't show error for stats, just log it
    }
  };

  const fetchActivityStats = async () => {
    try {
      const token = localStorage.getItem('partnerToken');
      if (!token) return;

      // Fetch inquiries
      const inquiriesRes = await api.get('/package-booking/partner/inquiries', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const inquiries = inquiriesRes.data.inquiries || [];
      setAllInquiries(inquiries);
      const pendingInquiries = inquiries.filter(i => i.status === 'new').length;
      const unreadMessages = inquiries.filter(i => !i.partner_responded).length;

      // Fetch bookings
      const bookingsRes = await api.get('/package-booking/partner/bookings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bookings = bookingsRes.data.bookings || [];
      setAllBookings(bookings);
      const totalBookings = bookings.length;
      const pendingBookings = bookings.filter(b => b.status === 'pending').length;
      const awaitingPayment = bookings.filter(b => b.status === 'partner_confirmed').length;
      const confirmedBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'paid' || b.status === 'completed').length;

      setActivityStats({
        pendingInquiries,
        unreadMessages,
        pendingBookings,
        awaitingPayment,
        confirmedBookings,
        totalBookings
      });

      // Store recent bookings (sorted by created_at, most recent first)
      const sortedBookings = bookings.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ).slice(0, 5);
      setRecentBookings(sortedBookings);

      try {
        const flightsRes = await api.get('/partners/flight-alerts', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFlightAlertCount(flightsRes.data?.count || 0);
      } catch (flightErr) {
        console.error('Error fetching partner flight alerts:', flightErr);
        setFlightAlertCount(0);
      }
    } catch (err) {
      console.error('Error fetching activity stats:', err);
    }
  };

  const fetchRecentPackages = async () => {
    try {
      const token = localStorage.getItem('partnerToken');
      if (!token) return;

      const response = await api.get('/partner-packages/my-packages', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const packages = response.data.packages || [];
      setRecentPackages(packages.slice(0, 3));
      
      // Set package stats
      setPackageStats({
        totalPackages: packages.length,
        publishedPackages: packages.filter(p => p.status === 'published').length
      });
    } catch (err) {
      console.error('Error fetching recent packages:', err);
    }
  };

  const fetchCommissionSummary = async () => {
    try {
      const token = localStorage.getItem('partnerToken');
      if (!token) return;

      const response = await api.get('/partner-commission/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.summary) {
        setCommissionSummary(response.data.summary);
      }
    } catch (err) {
      console.error('Error fetching commission summary:', err);
    }
  };

  const fetchFinanceSummary = async () => {
    try {
      const token = localStorage.getItem('partnerToken');
      if (!token) return;
      const response = await api.get('/partners/finance/summary', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.summary) {
        setFinanceSummary(response.data.summary);
      }
    } catch (err) {
      console.error('Error fetching finance summary:', err);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('partnerToken');
      await api.post('/partners/logout', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Error logging out:', err);
    } finally {
      localStorage.removeItem('partnerToken');
      localStorage.removeItem('partner');
      clearClientSessionBranding();
      navigate('/');
    }
  };

  const getBusinessTypeLabel = (type) => {
    const types = {
      hotel: 'Hotel / Accommodation',
      restaurant: 'Restaurant / Food Service',
      tour_operator: 'Tour Operator',
      transport: 'Transportation Service',
      activity_provider: 'Activity Provider',
      other: 'Other'
    };
    return types[type] || type;
  };

  const formatDateRange = (start, end) => {
    const startDate = parseTripDate(start);
    if (!startDate) return '';
    const endDate = parseTripDate(end);
    if (!endDate) return format(startDate, 'd MMM yyyy');
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    return `${format(startDate, sameYear ? 'd MMM' : 'd MMM yyyy')} – ${format(endDate, 'd MMM yyyy')}`;
  };

  const inProgressTrips = recentTrips.filter((trip) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = parseTripDate(trip.end_date);
    return Boolean(endDate) && endDate >= today;
  }).slice(0, 4);

  const oldestPendingBooking = recentBookings
    .filter((booking) => booking.status === 'pending')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];

  const bookingStatusLabel = (status) => {
    const labels = {
      pending: t('partnerDash.awaitingConfirmation'),
      partner_confirmed: t('partnerDash.awaitingConfirmation'),
      paid: 'Paid',
      confirmed: 'Confirmed',
      completed: 'Complete',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  const tripSubtitle = (trip) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    if (startDate <= today && endDate >= today) return 'Active';
    if (endDate < today) return 'Completed';
    return 'Upcoming';
  };

  const clientLimit = formatPlanLimit(entitlements?.limits?.maxClientTrips, t('partnerPlans.unlimited'));
  const groupLimit = formatPlanLimit(entitlements?.limits?.maxGroupTrips, t('partnerPlans.unlimited'));

  if (loading) {
    return (
      <Box sx={{ bgcolor: DOCUMENT.canvas, py: 8, minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: DOCUMENT.canvas, py: 8, px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const workspaceBrand = getPartnerWorkspaceBrand(partner, entitlements);
  const pageInk = workspaceBrand?.ink || DOCUMENT.ink;
  const pageAccent = workspaceBrand?.accent || DOCUMENT.accent;

  const linkSx = {
    fontSize: 11,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: DOCUMENT.ink2,
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    pb: '2px',
    cursor: 'pointer',
    background: 'none',
    borderLeft: 0,
    borderRight: 0,
    borderTop: 0,
    p: 0,
    fontFamily: 'inherit',
    '&:hover': { color: pageInk, borderBottomColor: pageAccent },
  };

  const sectionHeadSx = {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px 14px',
    flexWrap: 'wrap',
    pb: 1.25,
    borderBottom: `1px solid ${DOCUMENT.ink}`,
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          bgcolor: workspaceBrand?.canvas || DOCUMENT.canvas,
          minHeight: '100vh',
          color: pageInk,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            pl: 2,
            pr: 2,
            ...partnerMobileBottomPadSx,
          }}
        >
          <PartnerMobileHome
            trips={allTrips}
            bookings={allBookings}
            inquiries={allInquiries}
            flightAlerts={flightAlertCount}
          />
          <PartnerNeedsAttention
            items={attentionItems}
            loading={attentionLoading}
            sectionHeadSx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '8px 14px',
              flexWrap: 'wrap',
              pb: 1.25,
              borderBottom: `1px solid ${DOCUMENT.ink}`,
              mt: 3.5,
            }}
            onChanged={fetchOperationsAttention}
            onView={(item) => {
              if (item.inquiryId) {
                navigate(`/partner/inquiries/${item.inquiryId}`);
              } else if (item.requestId) {
                setSelectedAttention(item);
                setRequestDrawerOpen(true);
              } else if (item.tripId) {
                navigate(`/partner/trips/${item.tripId}`);
              }
            }}
            onAssign={(item) => {
              if (item.inquiryId) {
                navigate(`/partner/inquiries/${item.inquiryId}`);
              } else if (item.requestId) {
                setSelectedAttention(item);
                setRequestDrawerOpen(true);
              } else {
                navigate('/partner/requests');
              }
            }}
          />
          <PartnerTripRequestDrawer
            open={requestDrawerOpen}
            requestId={selectedAttention?.requestId || null}
            onClose={() => {
              setRequestDrawerOpen(false);
              setSelectedAttention(null);
            }}
            onChanged={fetchOperationsAttention}
          />
          <PartnerWorkspaceFooter />
        </Box>
        <PartnerPlanWall
          open={planWallOpen}
          onClose={() => setPlanWallOpen(false)}
          onSuccess={() => {
            setPlanWallOpen(false);
            fetchPartnerPlan();
          }}
          reason={planWallReason}
          entitlements={entitlements}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: workspaceBrand?.canvas || DOCUMENT.canvas, minHeight: '100vh', color: pageInk, overflowX: 'hidden' }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          pb: 8,
          pl: { xs: 2, sm: 3, md: `calc(${PARTNER_SIDEBAR_WIDTH}px + 40px)` },
          pr: { xs: 2, sm: 3, md: 5 },
        }}
      >      <PartnerPageHeader
        title={t('partnerDash.title')}
        subtitle={t('partnerDash.opsSubtitle', {
          type: getBusinessTypeLabel(partner?.business_type),
          plan: entitlements ? t(planNameKey(entitlements.tier)) : t('partnerPlans.starter'),
        })}
        actions={(
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
            <Box component="button" onClick={() => navigate('/partner/profile')} sx={linkSx}>{t('partnerDash.profile')}</Box>
            <Box component="button" onClick={() => setPlanWallOpen(true)} sx={linkSx}>{t('partnerDash.planLink')}</Box>
            <Box
              component="a"
              href={developerPublicBase()}
              sx={linkSx}
            >
              {t('partnerDash.developer')}
            </Box>
            <Box component="button" onClick={handleLogout} sx={linkSx}>{t('partnerDash.signOut')}</Box>
          </Box>
        )}
      />

      {parseFloat(commissionSummary.pending_commission_owed || 0) > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px 16px',
            flexWrap: 'wrap',
            mt: 3,
            px: 2.25,
            py: 1.5,
            bgcolor: DOCUMENT.surface,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            borderLeft: '2px solid #9C7B33',
          }}
        >
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink }}>
            {t('partnerDash.commissionPending', { amount: parseFloat(commissionSummary.pending_commission_owed).toFixed(2) })}
          </Typography>
        </Box>
      )}

      {activityStats.pendingBookings > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px 16px',
            flexWrap: 'wrap',
            mt: 3,
            px: 2.25,
            py: 1.5,
            bgcolor: DOCUMENT.surface,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            borderLeft: '2px solid #8C2B22',
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 400, color: DOCUMENT.ink }}>
            {t('partnerDash.attentionBookings', { count: activityStats.pendingBookings })}
          </Typography>
          {oldestPendingBooking?.created_at && (
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink2 }}>
              {t('partnerDash.oldestReceived', { when: formatDistanceToNow(parseISO(String(oldestPendingBooking.created_at).replace(' ', 'T')), { addSuffix: true }) })}
            </Typography>
          )}
          <Box
            component="button"
            onClick={() => navigate('/partner/bookings')}
            sx={{ ...linkSx, ml: { sm: 'auto' }, borderBottomColor: DOCUMENT.accent, color: DOCUMENT.ink }}
          >
            {t('partnerDash.review')}
          </Box>
        </Box>
      )}

      {/* Operations — command center */}
      <PartnerDesktopOpsSection
        trips={allTrips}
        bookings={allBookings}
        inquiries={allInquiries}
        awaitingPayment={activityStats.awaitingPayment}
        publishedPackages={packageStats.publishedPackages}
        flightAlerts={flightAlertCount}
        openRequests={openRequestCount}
        sectionHeadSx={sectionHeadSx}
        navigate={navigate}
        t={t}
      />

      <PartnerNeedsAttention
        items={attentionItems}
        loading={attentionLoading}
        sectionHeadSx={sectionHeadSx}
        onChanged={fetchOperationsAttention}
        onView={(item) => {
          if (item.inquiryId) {
            navigate(`/partner/inquiries/${item.inquiryId}`);
          } else if (item.requestId) {
            setSelectedAttention(item);
            setRequestDrawerOpen(true);
          } else if (item.tripId) {
            navigate(`/partner/trips/${item.tripId}`);
          }
        }}
        onAssign={(item) => {
          if (item.inquiryId) {
            navigate(`/partner/inquiries/${item.inquiryId}`);
          } else if (item.requestId) {
            setSelectedAttention(item);
            setRequestDrawerOpen(true);
          } else {
            navigate('/partner/requests');
          }
        }}
      />

      <PartnerTripRequestDrawer
        open={requestDrawerOpen}
        requestId={selectedAttention?.requestId || null}
        onClose={() => {
          setRequestDrawerOpen(false);
          setSelectedAttention(null);
        }}
        onChanged={fetchOperationsAttention}
      />

      <Box sx={{ mt: 4 }} data-testid="partner-finance-summary">
        <Box sx={sectionHeadSx}>
          <Typography component="h2" sx={documentSectionTitleSx}>
            {t('partnerFinance.dashTitle')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
            {t('partnerFinance.dashSub')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: '1px',
            bgcolor: DOCUMENT.ruleHard,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            borderTop: 0,
          }}
        >
          {[
            {
              label: t('partnerFinance.bookingsThisMonth'),
              value: financeSummary.bookingsThisMonth,
            },
            {
              label: t('partnerFinance.outstandingPayments'),
              value: financeSummary.outstandingClientPayments,
            },
            {
              label: t('partnerFinance.expectedCommissions'),
              value: financeSummary.expectedCommissions,
            },
          ].map((tile) => (
            <Box
              key={tile.label}
              sx={{
                bgcolor: DOCUMENT.surface,
                px: 2.25,
                py: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: DOCUMENT.canvas },
              }}
              onClick={() => navigate('/partner/trips')}
            >
              <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                {tile.label}
              </Typography>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, mt: 0.75, color: DOCUMENT.ink }}>
                {`$${Number(tile.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Start something */}
      <Box sx={{ mt: 4 }}>
        <Box sx={sectionHeadSx}>
          <Typography component="h2" sx={documentSectionTitleSx}>{t('partnerDash.startSomething')}</Typography>
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>{t('partnerDash.startSub')}</Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: '1px',
            bgcolor: DOCUMENT.ruleHard,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            borderTop: 0,
          }}
        >
          {[
            { id: 'client', eyebrow: t('partnerDash.individual'), title: t('partnerDash.clientTrip'), desc: t('partnerDash.clientTripDesc'), go: t('partnerDash.create'), to: '/trips/create', locked: false, reason: 'billing' },
            { id: 'group', eyebrow: t('partnerDash.shared'), title: t('partnerDash.groupTrip'), desc: t('partnerDash.groupTripDesc'), go: t('partnerDash.create'), to: '/trips/create?mode=group', locked: canCreatePartnerGroupTrips(entitlements) === false, reason: 'group' },
            { id: 'package', eyebrow: t('partnerDash.bookable'), title: t('partnerDash.package'), desc: t('partnerDash.packageDesc'), go: t('partnerDash.create'), to: '/partner/packages/create', locked: false, reason: 'billing' },
            { id: 'duplicate', eyebrow: t('partnerDash.fromTemplate'), title: t('partnerDash.duplicateTrip'), desc: t('partnerDash.duplicateTripDesc'), go: t('partnerDash.browse'), to: '/partner/trips', locked: false, reason: 'billing' },
          ].map((tile) => (
            <Box
              key={tile.id}
              component="button"
              data-testid={`partner-start-tile-${tile.id}`}
              aria-label={tile.locked ? `${tile.title} — ${t('partnerDash.upgradeToUnlock')}` : tile.title}
              onClick={() => {
                if (tile.locked) {
                  setPlanWallReason(tile.reason);
                  setPlanWallOpen(true);
                  return;
                }
                navigate(tile.to);
              }}
              sx={{
                bgcolor: DOCUMENT.surface,
                p: { xs: '20px 18px 18px', md: '24px 20px 22px' },
                textAlign: 'left',
                border: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: 'inherit',
                opacity: tile.locked ? 0.72 : 1,
                '&:hover': { bgcolor: tile.locked ? DOCUMENT.surface : '#F5F0E6' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: DOCUMENT.accent }}>{tile.eyebrow}</Typography>
                {tile.locked && (
                  <LockOutlinedIcon sx={{ fontSize: 13, color: DOCUMENT.ink3 }} aria-hidden />
                )}
              </Box>
              <Typography sx={{ display: 'block', fontFamily: DOCUMENT.display, fontSize: { xs: 20, md: 22 }, fontWeight: 400, mt: 1.25, mb: 0.75, lineHeight: 1.15 }}>{tile.title}</Typography>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, lineHeight: 1.6 }}>{tile.desc}</Typography>
              <Typography sx={{ display: 'block', mt: 1.75, fontSize: 10.5, letterSpacing: '0.15em', textTransform: 'uppercase', color: DOCUMENT.accent }}>
                {tile.locked ? t('partnerDash.upgradeToUnlock') : tile.go}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* In progress */}
      <Box sx={{ mt: 5.5 }}>
        <Box sx={{ ...sectionHeadSx, borderBottomColor: DOCUMENT.ruleHard }}>
          <Typography component="h2" sx={documentSubSectionTitleSx}>{t('partnerDash.inProgress')}</Typography>
          <Typography sx={{ fontSize: 10, letterSpacing: '0.17em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
            {t('partnerDash.tripsCount', { count: inProgressTrips.length })}
          </Typography>
          <Box component="button" onClick={() => navigate('/partner/trips')} sx={{ ...linkSx, ml: 'auto' }}>{t('partnerDash.allTrips')}</Box>
        </Box>
        {inProgressTrips.length === 0 ? (
          <Typography sx={{ py: 3, fontSize: 13.5, color: DOCUMENT.ink3 }}>{t('partnerDash.noTrips')}</Typography>
        ) : inProgressTrips.map((trip) => {
          const clientName = trip.owner_first_name && trip.owner_last_name
            ? `${trip.owner_first_name} ${trip.owner_last_name}`
            : trip.owner_username || trip.owner_email || 'Client';
          return (
            <Box
              key={trip.id}
              sx={{
                display: { xs: 'block', md: 'grid' },
                gridTemplateColumns: { md: '1fr 200px 145px 135px auto' },
                gap: { md: '22px' },
                alignItems: 'center',
                py: { xs: 2.5, md: 2.25 },
                borderBottom: `1px solid ${DOCUMENT.rule}`,
              }}
            >
              <Box>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 19, md: 20 }, lineHeight: 1.2 }}>
                  {trip.name || trip.title}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, mt: 0.5 }}>{tripSubtitle(trip)}</Typography>
              </Box>
              <Box sx={{ mt: { xs: 1.25, md: 0 } }}>
                <Typography sx={{ fontSize: 13.5 }}>{clientName}</Typography>
                {trip.owner_email && <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, mt: 0.25 }}>{trip.owner_email}</Typography>}
              </Box>
              <Typography sx={{ mt: { xs: 0.5, md: 0 }, fontSize: 13, color: DOCUMENT.ink2, fontVariantNumeric: 'tabular-nums' }}>
                {formatDateRange(trip.start_date, trip.end_date)}
              </Typography>
              <Typography sx={{ mt: { xs: 1.5, md: 0 }, fontSize: 12.5, color: DOCUMENT.ink2 }}>
                {trip.description ? `${trip.description.substring(0, 42)}${trip.description.length > 42 ? '…' : ''}` : tripSubtitle(trip)}
              </Typography>
              <Box sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1.75, md: 0 } }}>
                <Button
                  onClick={() => navigate(`/partner/trips/${trip.id}`)}
                  sx={{
                    bgcolor: DOCUMENT.oxblood,
                    color: DOCUMENT.ivory,
                    borderRadius: 0,
                    px: 2.25,
                    py: 1,
                    fontSize: 11,
                    letterSpacing: '0.11em',
                    textTransform: 'uppercase',
                    '&:hover': { bgcolor: DOCUMENT.nav },
                  }}
                >
                  {t('partnerDash.openTrip')}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Recent bookings */}
      <Box sx={{ mt: 5.5 }}>
        <Box sx={{ ...sectionHeadSx, borderBottomColor: DOCUMENT.ruleHard }}>
          <Typography component="h2" sx={documentSubSectionTitleSx}>{t('partnerDash.recentBookings')}</Typography>
          <Box component="button" onClick={() => navigate('/partner/bookings')} sx={{ ...linkSx, ml: 'auto' }}>{t('partnerDash.allBookings')}</Box>
        </Box>
        {recentBookings.length === 0 ? (
          <Typography sx={{ py: 3, fontSize: 13.5, color: DOCUMENT.ink3 }}>{t('partnerDash.noBookings')}</Typography>
        ) : (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', display: { xs: 'block', sm: 'table' } }}>
            <Box component="thead" sx={{ display: { xs: 'none', sm: 'table-header-group' } }}>
              <Box component="tr">
                {['Reference', 'Customer', 'Package', 'Travel dates', 'Status'].map((heading) => (
                  <Box
                    key={heading}
                    component="th"
                    sx={{
                      textAlign: heading === 'Status' ? 'right' : 'left',
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: '0.19em',
                      textTransform: 'uppercase',
                      color: DOCUMENT.ink3,
                      pt: 1.75,
                      pb: 1.25,
                      px: 2,
                      borderBottom: `1px solid ${DOCUMENT.rule}`,
                      '&:first-of-type': { pl: 0 },
                      '&:last-of-type': { pr: 0 },
                    }}
                  >
                    {heading}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody" sx={{ display: { xs: 'block', sm: 'table-row-group' } }}>
              {recentBookings.map((booking) => (
                <Box
                  key={booking.id}
                  component="tr"
                  onClick={() => navigate('/partner/bookings')}
                  sx={{
                    cursor: 'pointer',
                    display: { xs: 'grid', sm: 'table-row' },
                    gridTemplateColumns: '1fr auto',
                    py: { xs: 2, sm: 0 },
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                    '&:hover td': { bgcolor: '#F5F0E6' },
                  }}
                >
                  <Box component="td" sx={{ py: { sm: 1.75 }, pr: 2, fontSize: 13.5, verticalAlign: 'top', pl: 0 }}>
                    <Typography sx={{ fontWeight: 400 }}>{booking.booking_reference}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, mt: 0.25 }}>
                      {booking.created_at ? format(new Date(booking.created_at), 'd MMM yyyy') : ''}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ py: { sm: 1.75 }, px: 2, fontSize: 13.5, verticalAlign: 'top' }}>
                    <Typography>{booking.user_name || booking.user_email || 'Customer'}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, mt: 0.25 }}>{booking.user_email}</Typography>
                  </Box>
                  <Box component="td" sx={{ py: { sm: 1.75 }, px: 2, fontSize: 13.5, verticalAlign: 'top' }}>
                    <Typography>{booking.package_title}</Typography>
                    {booking.package_trip_code && (
                      <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, mt: 0.25 }}>{booking.package_trip_code}</Typography>
                    )}
                  </Box>
                  <Box component="td" sx={{ display: { xs: 'none', sm: 'table-cell' }, py: 1.75, px: 2, fontSize: 13.5, fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' }}>
                    {booking.travel_date ? formatDateRange(booking.travel_date, booking.return_date) : '—'}
                  </Box>
                  <Box component="td" sx={{ py: { sm: 1.75 }, pl: 2, pr: 0, fontSize: 12, color: booking.status === 'pending' || booking.status === 'partner_confirmed' ? '#8C2B22' : DOCUMENT.ink2, textAlign: { sm: 'right' }, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {bookingStatusLabel(booking.status)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* KPIs */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          mt: 5.5,
          borderTop: `1px solid ${DOCUMENT.ruleHard}`,
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        }}
      >
        {[
          { label: t('partnerDash.clientTripsKpi'), value: entitlements?.usage?.clientTrips ?? stats.activeTrips, suffix: entitlements ? t('partnerDash.ofLimit', { limit: clientLimit }) : null },
          { label: t('partnerDash.groupTripsKpi'), value: entitlements?.usage?.groupTrips ?? 0, suffix: entitlements ? t('partnerDash.ofLimit', { limit: groupLimit }) : null },
          { label: t('partnerDash.packagesLive'), value: packageStats.publishedPackages },
          { label: t('partnerDash.openInquiries'), value: activityStats.pendingInquiries },
          { label: t('partnerDash.travellersBooked'), value: activityStats.confirmedBookings },
          { label: t('partnerDash.plan'), value: entitlements ? t(planNameKey(entitlements.tier)) : t('partnerPlans.starter') },
        ].map((kpi) => (
          <Box key={kpi.label} sx={{ px: { xs: 1.75, md: 2.5 }, py: 1.75, borderRight: `1px solid ${DOCUMENT.rule}`, '&:last-of-type': { borderRight: 0 } }}>
            <Typography sx={{ fontSize: 10, letterSpacing: '0.17em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.75, lineHeight: 1.35 }}>{kpi.label}</Typography>
            <Typography sx={{ fontSize: 19, fontVariantNumeric: 'tabular-nums' }}>
              {kpi.value}
              {kpi.suffix && (
                <Box component="em" sx={{ fontStyle: 'normal', fontSize: 12, color: DOCUMENT.ink3, ml: 0.5 }}>{kpi.suffix}</Box>
              )}
            </Typography>
          </Box>
        ))}
      </Box>

      <PartnerWorkspaceFooter />
      </Box>

      <PartnerPlanWall
        open={planWallOpen}
        onClose={() => setPlanWallOpen(false)}
        onSuccess={() => {
          setPlanWallOpen(false);
          fetchPartnerPlan();
        }}
        reason={planWallReason}
        entitlements={entitlements}
      />
    </Box>
  );
};

export default PartnerDashboard;
